import React, { useState, useMemo } from 'react';
import { useApp, previewDocNo, hasPrefix } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { formatCurrency, formatDate, today } from '../../utils/helpers';
import { validators, validateForm } from '../../utils/validation';

const emptyForm = { date: today(), ledgerAccount: '', amount: 0, mode: 'Cash', bankAccount: '', chequeNo: '', bankDetails: '', narration: '' };

// Direct (non-party) Payment — money paid from Cash/Bank against a general
// ledger account (e.g. bank charges, direct expenses, drawings, loan repayment).
export default function DirectPaymentEntry() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const payAccountName = form.mode === 'Cash' ? 'Cash' : form.bankAccount;
  const payAccount = state.accounts.find(a => a.name === payAccountName);

  // Ledger accounts for a non-party voucher must never include party ledgers.
  // A party ledger is any account that: is a party's linked ledger, shares a
  // party's name, or sits under a Sundry Debtors/Creditors (party) group.
  const ledgerAccounts = useMemo(() => {
    const norm = (s) => (s || '').trim().toLowerCase();
    const partyNames = new Set((state.parties || []).map(p => norm(p.name)));
    const partyRefs = new Set((state.parties || []).map(p => p.linkedLedger).filter(Boolean));
    const isPartyGroup = (a) => {
      const g = (state.accountGroups || []).find(x => x.id === a.groupId);
      return /debtor|creditor|sundry|party/i.test(`${g?.accountName || ''} ${g?.groupName || ''} ${a.group || ''}`);
    };
    return state.accounts.filter(a =>
      !partyNames.has(norm(a.name)) && !partyRefs.has(a.id) && !partyRefs.has(a.name) && !isPartyGroup(a)
    );
  }, [state.accounts, state.parties, state.accountGroups]);

  const errors = useMemo(() => {
    const rules = {
      date: [validators.required(form.date, 'Date')],
      ledgerAccount: [validators.required(form.ledgerAccount, 'Ledger account')],
      amount: [
        validators.required(form.amount, 'Amount'),
        validators.positiveNonZero(form.amount, 'Amount'),
        validators.maxValue(form.amount, 99999999, 'Amount'),
      ],
      narration: [validators.maxLength(form.narration, 500, 'Narration')],
    };
    if (form.mode !== 'Cash') rules.bankAccount = [validators.required(form.bankAccount, 'Bank account')];
    if (form.mode === 'Cheque') { rules.chequeNo = [validators.required(form.chequeNo, 'Cheque number')]; rules.bankDetails = [validators.required(form.bankDetails, 'Bank details')]; }
    if (form.mode === 'NEFT') rules.chequeNo = [validators.required(form.chequeNo, 'Reference number')];
    return validateForm(rules).errors;
  }, [form]);

  const hasErrors = Object.values(errors).some(e => e);
  const touch = (f) => setTouched(prev => ({ ...prev, [f]: true }));

  const prefixMissing = !editData && !hasPrefix(state, 'directPayment', form.date);

  const handleSave = async () => {
    if (prefixMissing) { alert('No Prefix Setting found for Other Payment Entry. Please set the prefix first in Masters → Prefix Setting (effective on the selected date) before saving.'); return; }
    setSubmitted(true);
    setTouched({ date: true, ledgerAccount: true, amount: true, bankAccount: true, chequeNo: true, bankDetails: true });
    if (hasErrors) return;
    if (payAccount && (Number(payAccount.balance) || 0) < Number(form.amount)) {
      const ok = await confirm(`${payAccountName} balance (${formatCurrency(payAccount.balance || 0)}) is less than the amount. The balance will go negative.`, { title: 'Low Balance', variant: 'warning', confirmLabel: 'Continue Anyway' });
      if (!ok) return;
    }
    if (editData) dispatch({ type: 'DELETE_DIRECT_PAYMENT', payload: editData.id });
    dispatch({ type: 'ADD_DIRECT_PAYMENT', payload: { ...form } });
    addToast('Payment saved successfully', 'success');
    closeForm();
  };

  const handleEdit = (row) => {
    setForm({ date: row.date, ledgerAccount: row.ledgerAccount, amount: row.amount, mode: row.mode, bankAccount: row.bankAccount || '', chequeNo: row.chequeNo || '', bankDetails: row.bankDetails || '', narration: row.narration || '' });
    setEditData(row); setTouched({}); setSubmitted(false); setShowForm(true);
  };

  const handleDelete = (row) => {
    if (window.confirm(`Delete voucher ${row.voucherNo}? Account balances will be reversed.`)) {
      dispatch({ type: 'DELETE_DIRECT_PAYMENT', payload: row.id });
    }
  };

  const closeForm = () => { setShowForm(false); setForm(emptyForm); setEditData(null); setTouched({}); setSubmitted(false); };

  const columns = [
    { key: 'voucherNo', label: 'Voucher No', sortable: true },
    { key: 'date', label: 'Date', render: (v) => formatDate(v), exportValue: r => formatDate(r.date) },
    { key: 'ledgerAccount', label: 'Paid Towards A/c', render: (v) => v || '-' },
    { key: 'mode', label: 'Mode', render: (v) => <span className="badge badge-gray">{v}</span> },
    { key: 'amount', label: 'Amount', align: 'right', render: (v) => formatCurrency(v) },
    { key: 'narration', label: 'Narration' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Other Payment Entry</h1>
        <button onClick={() => { setForm(emptyForm); setEditData(null); setTouched({}); setSubmitted(false); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Payment
        </button>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.directPayments].reverse()}
          searchFields={['voucherNo', 'ledgerAccount', 'narration']}
          exportFilename="Direct_Payments"
          emptyState={{ title: 'No non-party payments yet', description: 'Record money paid from Cash/Bank against a ledger account (bank charges, direct expenses, drawings, etc.).', actionLabel: 'New Payment', onAction: () => { setForm(emptyForm); setTouched({}); setSubmitted(false); setShowForm(true); } }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={closeForm} title={editData ? `Edit ${editData.voucherNo}` : 'New Payment (Non-Party)'}>
        <ErrorSummary errors={errors} show={submitted && hasErrors} />
        {prefixMissing && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            No Prefix Setting for Other Payment Entry on this date. Please set the prefix first in <b>Masters → Prefix Setting</b> before saving.
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Voucher No">
            <input className="input bg-gray-100 font-semibold text-blue-700" value={editData ? editData.voucherNo : previewDocNo(state, 'directPayment', 'directPayments', form.date, 'DPV')} readOnly placeholder="Set Prefix first" />
          </FormField>

          <FormField label="Date" required error={errors.date} touched={touched.date || submitted}>
            <input className="input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} onBlur={() => touch('date')} max={today()} />
          </FormField>

          <FormField label="Mode" required>
            <select className="input" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value, bankAccount: e.target.value === 'Cash' ? '' : form.bankAccount })}>
              <option>Cash</option>
              <option>Cheque</option>
              <option>NEFT</option>
              <option>UPI</option>
            </select>
          </FormField>

          {form.mode !== 'Cash' ? (
            <FormField label="Paid From Bank" required error={errors.bankAccount} touched={touched.bankAccount || submitted} hint="Bank account the money leaves">
              <select className="input" value={form.bankAccount} onChange={e => setForm({ ...form, bankAccount: e.target.value })} onBlur={() => touch('bankAccount')}>
                <option value="">-- Select Bank --</option>
                {state.accounts.filter(a => a.type === 'Bank').map(a => (
                  <option key={a.id} value={a.name}>{a.name} (Balance: {formatCurrency(a.balance)})</option>
                ))}
              </select>
            </FormField>
          ) : <div />}

          <FormField label="Amount" required error={errors.amount} touched={touched.amount || submitted}>
            <input className="input" type="number" min="0.01" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: Number(e.target.value) })} onBlur={() => touch('amount')} />
          </FormField>

          <FormField label="Paid Towards (Ledger A/c)" required error={errors.ledgerAccount} touched={touched.ledgerAccount || submitted} hint="Bank / general ledger account being debited">
            <select className="input" value={form.ledgerAccount} onChange={e => setForm({ ...form, ledgerAccount: e.target.value })} onBlur={() => touch('ledgerAccount')}>
              <option value="">-- Select Account --</option>
              {ledgerAccounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </FormField>

          {(form.mode === 'Cheque' || form.mode === 'NEFT') && (
            <>
              <FormField label="Bank Details" required={form.mode === 'Cheque'} error={errors.bankDetails} touched={touched.bankDetails || submitted}>
                <input className="input" value={form.bankDetails} onChange={e => setForm({ ...form, bankDetails: e.target.value })} onBlur={() => touch('bankDetails')} placeholder="Bank name & branch" />
              </FormField>
              <FormField label={form.mode === 'Cheque' ? 'Cheque No' : 'Reference No'} required error={errors.chequeNo} touched={touched.chequeNo || submitted}>
                <input className="input" value={form.chequeNo} onChange={e => setForm({ ...form, chequeNo: e.target.value })} onBlur={() => touch('chequeNo')} />
              </FormField>
            </>
          )}

          <div className="col-span-2">
            <FormField label="Narration" error={errors.narration} touched={touched.narration || submitted}>
              <textarea className="input" rows={2} value={form.narration} onChange={e => setForm({ ...form, narration: e.target.value })} onBlur={() => touch('narration')} maxLength={500} placeholder="e.g. Bank charges / Owner drawings" />
            </FormField>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeForm} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={(submitted && hasErrors) || prefixMissing}>{editData ? 'Update' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}
